import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import PrivacyConsents, {
  emptyPrivacyConsents,
  PrivacyConsentValues,
} from "./PrivacyConsents";
import { persistUtmFromUrl, submitFormRecall } from "../utils/form-submissions";

const BRAND = "serviziobolletta.it";

interface FormData {
  nome: string;
  cognome: string;
  email: string;
  telefono: string;
  servizio: string;
  messaggio: string;
}

type FormStatus = "idle" | "loading" | "success" | "error";

const ContactForm: React.FC = () => {
  const formRef = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState<FormData>({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    servizio: "",
    messaggio: "",
  });

  const [errors, setErrors] = useState<Partial<FormData & { privacy1: string }>>({});
  const [consents, setConsents] = useState<PrivacyConsentValues>(emptyPrivacyConsents());
  const [formStatus, setFormStatus] = useState<FormStatus>("idle");

  useEffect(() => {
    persistUtmFromUrl();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (errors[name as keyof FormData]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData & { privacy1: string }> = {};

    if (!formData.nome.trim()) newErrors.nome = "Il nome è obbligatorio";
    if (!formData.cognome.trim()) newErrors.cognome = "Il cognome è obbligatorio";
    if (!formData.email.trim()) {
      newErrors.email = "L'email è obbligatoria";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = "Formato email non valido";
    }

    const tel = formData.telefono.replace(/[\s\-().+]/g, "");
    if (!tel) {
      newErrors.telefono = "Il telefono è obbligatorio";
    } else if (!/^\d{9,13}$/.test(tel)) {
      newErrors.telefono = "Inserisci un numero valido (min. 9 cifre)";
    }

    if (!formData.messaggio.trim()) newErrors.messaggio = "Il messaggio è obbligatorio";

    if (!consents.privacy1) {
      newErrors.privacy1 = "Devi accettare l'informativa privacy e i termini e condizioni";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) return;

    setFormStatus("loading");

    const urlParams = new URLSearchParams(window.location.search);
    const idsms = urlParams.get("idsms");

    try {
      await submitFormRecall(
        {
          nome: formData.nome,
          cognome: formData.cognome,
          telefono: formData.telefono,
          privacy1: consents.privacy1,
          privacy2: consents.privacy2,
          privacy3: consents.privacy3,
        },
        {
          brand: BRAND,
          idsms,
          origine: "contact_form",
          extra: {
            email: formData.email.trim(),
            servizio: formData.servizio || undefined,
            messaggio: formData.messaggio.trim(),
          },
        },
      );

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "generate_lead",
        lead_source: "contact_form",
        lead_service: formData.servizio || "non_specificato",
      });

      setFormStatus("success");

      if (formRef.current) formRef.current.reset();
      setFormData({
        nome: "",
        cognome: "",
        email: "",
        telefono: "",
        servizio: "",
        messaggio: "",
      });
      setConsents(emptyPrivacyConsents());

      setTimeout(() => setFormStatus("idle"), 5000);
    } catch (error) {
      console.error("Errore nell'invio del form:", error);
      setFormStatus("error");
      setTimeout(() => setFormStatus("idle"), 5000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="lg:w-2/3"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="bg-white p-8 rounded-lg shadow-md"
      >
        <h2 className="text-3xl font-bold mb-4 text-primary">
          Richiedi una Consulenza Gratuita
        </h2>
        <p className="mb-2 text-light-muted">
          Compila il modulo per ricevere una consulenza gratuita e senza impegno.
        </p>
        <p className="mb-6 text-xs text-light-muted/70">
          Il servizio è completamente gratuito e non comporta alcun obbligo di
          sottoscrizione.
        </p>

        {formStatus === "success" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-green-100 text-green-700 rounded-md flex items-center"
          >
            <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
            Grazie per averci contattato! Ti ricontatteremo per una consulenza
            gratuita e senza impegno.
          </motion.div>
        )}

        {formStatus === "error" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-100 text-red-700 rounded-md flex items-center"
          >
            <FontAwesomeIcon icon={faExclamationCircle} className="mr-2" />
            Si è verificato un errore nell'invio del messaggio. Riprova più
            tardi o contattaci telefonicamente.
          </motion.div>
        )}

        <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <label
                htmlFor="nome"
                className="block mb-2 text-light-text font-medium"
              >
                Nome *
              </label>
              <input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                className={`w-full p-3 border ${
                  errors.nome ? "border-red-500" : "border-light-border"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50`}
                placeholder="Il tuo nome"
                required
              />
              {errors.nome && (
                <p className="mt-1 text-red-500 text-sm">{errors.nome}</p>
              )}
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <label
                htmlFor="cognome"
                className="block mb-2 text-light-text font-medium"
              >
                Cognome *
              </label>
              <input
                type="text"
                id="cognome"
                name="cognome"
                value={formData.cognome}
                onChange={handleChange}
                className={`w-full p-3 border ${
                  errors.cognome ? "border-red-500" : "border-light-border"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50`}
                placeholder="Il tuo cognome"
                required
              />
              {errors.cognome && (
                <p className="mt-1 text-red-500 text-sm">{errors.cognome}</p>
              )}
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <label
                htmlFor="email"
                className="block mb-2 text-light-text font-medium"
              >
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full p-3 border ${
                  errors.email ? "border-red-500" : "border-light-border"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50`}
                placeholder="La tua email"
                required
              />
              {errors.email && (
                <p className="mt-1 text-red-500 text-sm">{errors.email}</p>
              )}
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <label
                htmlFor="telefono"
                className="block mb-2 text-light-text font-medium"
              >
                Telefono *
              </label>
              <input
                type="tel"
                id="telefono"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                className={`w-full p-3 border ${
                  errors.telefono ? "border-red-500" : "border-light-border"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50`}
                placeholder="Il tuo numero di telefono"
                required
              />
              {errors.telefono && (
                <p className="mt-1 text-red-500 text-sm">{errors.telefono}</p>
              )}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <label
              htmlFor="servizio"
              className="block mb-2 text-light-text font-medium"
            >
              Servizio di interesse
            </label>
            <select
              id="servizio"
              name="servizio"
              value={formData.servizio}
              onChange={handleChange}
              className="w-full p-3 border border-light-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">Seleziona un servizio</option>
              <option value="luce">Offerte Luce</option>
              <option value="gas">Offerte Gas</option>
              <option value="internet">Offerte Internet</option>
              <option value="business">Soluzioni Business</option>
              <option value="altro">Altro</option>
            </select>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <label
              htmlFor="messaggio"
              className="block mb-2 text-light-text font-medium"
            >
              Messaggio *
            </label>
            <textarea
              id="messaggio"
              name="messaggio"
              value={formData.messaggio}
              onChange={handleChange}
              rows={5}
              className={`w-full p-3 border ${
                errors.messaggio ? "border-red-500" : "border-light-border"
              } rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50`}
              placeholder="Il tuo messaggio"
              required
            ></textarea>
            {errors.messaggio && (
              <p className="mt-1 text-red-500 text-sm">{errors.messaggio}</p>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            <PrivacyConsents
              idPrefix="contact"
              values={consents}
              onChange={(next) => {
                setConsents(next);
                if (next.privacy1) setErrors((er) => ({ ...er, privacy1: undefined }));
              }}
              privacy1Error={errors.privacy1}
              labelClassName="text-sm text-light-muted leading-relaxed"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <motion.button
              type="submit"
              disabled={formStatus === "loading"}
              className={`btn ${
                formStatus === "loading" ? "bg-gray-400" : "btn-primary"
              } w-full md:w-auto flex items-center justify-center`}
              whileHover={formStatus !== "loading" ? { scale: 1.05 } : {}}
              whileTap={formStatus !== "loading" ? { scale: 0.95 } : {}}
            >
              {formStatus === "loading" ? (
                <>
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="mr-2 animate-spin"
                  />
                  Invio in corso...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} className="mr-2" />
                  Richiedi Consulenza Gratuita
                </>
              )}
            </motion.button>
          </motion.div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default ContactForm;
